import { Button, Col, DatePicker, Input, message, Popconfirm, Row, Table, Tooltip, Typography, Card, Spin, Space } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { serverFetch } from "../../server";
import { strDate2Server, fmtDate, fmtDateTimeFull, fmtTime } from "../../extensions/utils";
import { tableRenderDate, tableRenderDateTime } from "../../extensions/table-render";
import { getPaymentVidName, getSyncStatusColor, orderStatuses, renderSyncStatus, ticketStatuses } from "../../extensions/helper";
import PaymentXReport from "./payment-x-report";
import PaymentZReport from "./payment-z-report";
import { AddFilter, FilterItem, HoverIcon, LogMonitor, SportTooltip } from "../../controls";
import { BtnSettingsTable, tblGetColumns } from "../../controls/table-settings.js/table-settings";
import { HelpButton, isMobile, menuSize } from "../../extensions/design";
const { Text } = Typography;
const tableName = "tickets";

const Tickets = ({externalFilter = null }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState({ searchText: '', dateBeg: dayjs(), dateEnd: dayjs()});
  const [vidReport, setVidReport] = useState();
  const divRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [addingFilter, setAddingFilter] = useState(true);

  useEffect(() => {
    if (externalFilter !== null) {
      const rawFilter = { ...(externalFilter || {}) };
      setSearchText(rawFilter.searchText ?? "");
      const nextFilter = { dateBeg: filter.dateBeg, dateEnd: filter.dateEnd, ...rawFilter};
      setFilter(nextFilter);
      getData(nextFilter);
    }
  }, [externalFilter]);

  useEffect(() => {
    if (isMobile)
      getData(filter);
  }, [filter]);

  const onTableSettingsChange = () => {
    setTableKey(prev => prev + 1); // Принудительно обновляем таблицу
  }
  const getData = (f = filter) => {
    setLoading(true);
    const params = new URLSearchParams();
    const paramMap = {
      status: "statuses",
    };
    Object.keys(paramMap).forEach((key) => {
      const field = f[key];
      if (!field) return;

      const val = field.value;
      if (val === undefined || val === null || val === "") return;

      const paramName = paramMap[key];
      if (Array.isArray(val)) {
        val.forEach((v) => params.append(paramName, v));
      } else {
        params.append(paramName, val);
      }
    });
    serverFetch(`ticket?begPer=${strDate2Server(f.dateBeg)}&endPer=${strDate2Server(f.dateEnd)}&${params.toString()}`)
      .then(data => {
        setLoading(false);
        setItems(data);
        onSearchChange({ searchText }, data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }
  useEffect(getData, []);

  const filterLabels = {
    status: "Статус",
  };

  const onSearchChange = (changed, sourceItems = items) => {
    const text = changed.searchText.toLowerCase();
    setSearchText(text);

    if (!text) {
      setFilteredItems(sourceItems);
      return;
    }

    const searchableFields = [
      "clientName",
      "userName",
      "paymentVidName",
    ];

    const filtered = sourceItems.filter((item) =>
      searchableFields.some((field) => {
        const val = item[field];
        return val && String(val).toLowerCase().includes(text);
      })
    );

    setFilteredItems(filtered);
  };

  const onChangeFilter = (value) => setFilter({...filter, ...value});

  const headerSize =  divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) ?? 0;

  const columns = [
    { dataIndex: "id", title: "#" },
    { dataIndex: "docDate", title: "Дата", render: tableRenderDate },
    { dataIndex: "vid", title: "Вид", render: getPaymentVidName },
    { dataIndex: "paymentVidName", title: "Вид оплаты" },
    { dataIndex: "syma", title: "Сумма" },
    { dataIndex: "clientName", title: "Клиент" },
    { dataIndex: "createdOn", title: "Создано", render: tableRenderDateTime },
    { dataIndex: "userName", title: "Пользователь" },
    {
      title: "Статус",
      render: (_, row) => (
        <Space size={6}>
          {renderSyncStatus(row)}
          {row?.url ? (
            <Tooltip title="Открыть ссылку">
              <a href={row.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <PaperClipOutlined />
              </a>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    { dataIndex: "externalId", title: "ext #" },
  ]

  const onSetStatus = status => {
    setLoading(true);
    serverFetch("Ticket/SetStatus", { method: "PUT", bodyData: { ticketId: selected, status }})
      .then(getData)
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  }

  const onCloseReport = refresh => {
    setVidReport();
    refresh && getData();
  }
  const filters = Object.entries(filter)
    .filter(([key, data]) => !["searchText", "dateBeg", "dateEnd"].includes(key))
    .map(([type, data]) => ({ type, ...data }));

  const filterOptions = [
    { type: "status", label: "Статус", inputType: "multiselect" },
  ];

  const onFilterChange = (changed) => {
    setFilter((prev) => ({ ...prev, ...changed }));
  };

  const getUniqueOptions = (field) => {
    const unique = Array.from(new Set(items.map(item => item[field]).filter(Boolean)));
    return unique.map(value => ({ label: value, value }));
  };
  const handleAddTopFilter = (type) => {
    if (filter[type]) return;
  
    const option = filterOptions.find(opt => opt.type === type);
    const inputType = option?.inputType;
  
    let initialValue = undefined;
    let options = undefined;

    if (inputType === "multiselect") {
    switch (type) {
      case "status":
        options = ticketStatuses;
        break;
      default:
        options = getUniqueOptions(type);
      }
    }

    onFilterChange({ [type]: { value: initialValue, inputType, options } });
    setAddingFilter(false);
  };

  const removeTopFilter = (type) => {
    const { [type]: _, ...rest } = filter;
    setFilter(rest);
  };
  const renderFilterItem = (key, data) => (
    <FilterItem
      key={key}
      type={data.inputType}
      label={filterLabels[key] || key}
      value={data.value}
      options={data.options}
      onChange={(val) => onFilterChange({ [key]: { ...data, value: val } })}
      onRemove={() => removeTopFilter(key)}
    />
  );

  return !isMobile ?
    (<>
      <div className="sport-form-title">
        <DatePicker format="DD.MM.YYYY" value={filter.dateBeg} onChange={(e) => onChangeFilter({dateBeg: e})} allowClear={false}/>
          <DatePicker format="DD.MM.YYYY" value={filter.dateEnd} onChange={(e) => onChangeFilter({dateEnd: e})} allowClear={false}/>
        <Input placeholder="Введите наименование, ФИО" value={searchText} onChange={(e) => onSearchChange({ searchText: e.target.value })}/>
        <Tooltip title="Поставить в очередь синхронизации для печати чека на ККМ">
          <Popconfirm title="Подтверждение" description="Поставить в очередь синхронизации для печати чека на ККМ?" onConfirm={() => onSetStatus(1)}>
            <Button disabled={!selected}>Синхр.</Button>
          </Popconfirm>
          </Tooltip>
        <Tooltip title="Отменить необходимость печати чека на ККМ">
          <Popconfirm title="Подтверждение" description="Отменить необходимость печати чека на ККМ?" onConfirm={() => onSetStatus(0)}>
            <Button disabled={!selected}>Несинхр.</Button>
          </Popconfirm>
        </Tooltip>
        <Tooltip title="Снять сменный отчет БЕЗ гашения (X отчет)">
          <Button onClick={() => setVidReport("x")}>X отчет</Button>
        </Tooltip>
        <Tooltip title="Снять сменный отчет С гашением (Z отчет)">
          <Button onClick={() => setVidReport("z")}>Z отчет</Button>
        </Tooltip>
        <LogMonitor vid={12} id={selected}/>
        <Button onClick={() => getData(filter)} style={{ width:"56px", padding:"0px"}}>
          <SportTooltip title="Обновить страницу">
            <HoverIcon src="/images/refresh.svg" hoverSrc="/images/refresh-white.svg" style={{width: 30}} alt="refresh"/>
          </SportTooltip>
        </Button>
        <Col><BtnSettingsTable name={tableName} columns={columns} onSettingsChange={onTableSettingsChange}/></Col>
        <HelpButton article="oplaty"/>
      </div>
      <Row style={{ marginLeft:"8px", marginBottom:"8px" }}>
        <div className="filter-container">
          {filters.length === 0 && !addingFilter && (
            <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
          )}
          {filters.map((f) => renderFilterItem(f.type, f))}
          {filters.length < filterOptions.length && (
            addingFilter ? (
              <AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>
            ) : (
              filters.length !== 0 && (
                <Button onClick={() => setAddingFilter(true)} type="dashed" className="filter-add-button">+</Button>
              )
            )
          )}
        </div>
      </Row>
      <div ref={divRef} style={{width: tableWidth}}>
        <Table key={tableKey} dataSource={filteredItems} loading={loading} columns={tblGetColumns(columns, tableName)} size="small" scroll={{ y: tableHeight }} pagination={false} rowKey="id"
          rowSelection={{ type: 'radio', selectedRowKeys: [selected], renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
          onRow={(record) => ({onClick: () => {setSelected(record.id)}})}
        />
      </div>
      {vidReport === "x" && <PaymentXReport onClose={onCloseReport}/>}
      {vidReport === "z" && <PaymentZReport onClose={onCloseReport}/>}
      </>) : loading ? (
      <div className="report-loading">
        <Spin size="large" />
      </div>) : (<div style={{paddingTop: "55px"}}>
      <div style={{display:"flex", justifyContent: "space-around", alignItems:"center"}}>
        <Text>Период с </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateBeg} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateBeg: e })} allowClear={false}/>
        <Text> по </Text>
        <DatePicker className="record-date-picker-mobile" value={filter.dateEnd} format="DD.MM.YYYY" onChange={e => onFilterChange({ dateEnd: e })} allowClear={false}/>
      </div>
      <div className="tickets-item-line-quad" style={{paddingRight: 0}}>
        <Popconfirm title="Подтверждение" description="Поставить в очередь синхронизации для печати чека на ККМ?" onConfirm={() => onSetStatus(1)}
          placement="bottom" getPopupContainer={(trigger) => trigger.closest('.tickets-item-line-quad')} overlayClassName="centered-popconfirm">
          <Button disabled={!selected}>Синхр.</Button>
        </Popconfirm>
        <Popconfirm title="Подтверждение" description="Отменить необходимость печати чека на ККМ?" onConfirm={() => onSetStatus(0)}
          placement="bottom" getPopupContainer={(trigger) => trigger.closest('.tickets-item-line-quad')} overlayClassName="centered-popconfirm">
          <Button disabled={!selected}>Несинхр.</Button>
        </Popconfirm>
        <Button onClick={() => setVidReport("x")}>X отч.</Button>
        <Button onClick={() => setVidReport("z")}>Z отч.</Button>
      </div>

      <div className="stock-scrollable-list" style={{ height: window.innerHeight - menuSize - 75 }}>
        {filteredItems.map(item => {
          const isPayment = item.vid === 1;
          const sum = Number(item.syma || 0).toFixed(2);
          const isSelected = selected === item.id;
          const syncBorderColor = getSyncStatusColor(item.status) || "#d9d9d9";

          return (
            <Card key={item.id} className="stock-card" style={{ borderLeft: `4px solid ${syncBorderColor}`, backgroundColor: isSelected ? "#eff2ee" : "#fff" }} onClick={() => setSelected(item.id)}>
              <div className="stock-osv-item-line-triple-non-even">
                <Text strong>{fmtDate(item.docDate)} в {fmtTime(item.createdOn)}</Text>
                <Text>{getPaymentVidName(item.vid)}</Text>
                <Text strong style={{ textAlign: "right" }}> #{item.id} </Text>
              </div>

              <div className="stock-osv-item-line-double">
                <Text style={{ textAlign: "left", fontWeight: 600, color: isPayment ? "#3f8600" : "#cf1322" }}> Сумма: {sum} </Text>
                <Text style={{textAlign: "right"}}>Вид оплаты: {item.paymentVidName}</Text>
              </div>

              <div className="stock-osv-item-line-double">
                <Text className="sender" style={{ textAlign: "left"}}> Клиент: {item.clientName || "-" }</Text>
                <Text className="sender" style={{ textAlign: "right"}}> Польз.: {item.userName || "-" }</Text>
              </div>

              <div className="stock-osv-item-line-double" style={{ gridTemplateColumns: "1fr auto", alignItems: "center" }}>
                <Text style={{ textAlign: "left" }}> Внеш. #: {item.externalId || "-" } </Text>
                {item?.url ? (
                  <Tooltip title="Открыть ссылку">
                    <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      <PaperClipOutlined />
                    </a>
                  </Tooltip>
                ) : <span />}
              </div>
            </Card>
          );
          })}

          {filteredItems.length === 0 && !loading && (
            <div style={{ textAlign: "center", marginTop: 20, fontSize: 14 }}> Нет данных </div>
          )}

        </div>
        {vidReport === "x" && <PaymentXReport onClose={onCloseReport}/>}
        {vidReport === "z" && <PaymentZReport onClose={onCloseReport}/>}
      </div>
      )
}

export default Tickets;
